import { Link } from 'react-router-dom';
import { ArrowRight, BarChart2, Zap, Cpu } from 'lucide-react';
import RfPageFrame from '../layout/RfPageFrame';

export default function SolutionsAiSecurity() {
  return (
    <>
      <div className="rf-hero-band">
        <RfPageFrame className="!py-10 md:!py-14">
          <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5c6d7e] mb-2">Solutions · Overview</p>
          <h1 className="text-2xl md:text-[1.85rem] font-semibold text-[#2c2c2c] tracking-tight max-w-3xl">
            Product controls for secure, governable AI
          </h1>
          <p className="mt-4 text-[#5c5c5c] max-w-2xl leading-relaxed">
            Three solution families:{' '}
            <strong className="font-semibold text-[#2c2c2c]">Governance</strong> (policy, oversight, assessment),{' '}
            <strong className="font-semibold text-[#2c2c2c]">Security for AI</strong> (runtime and model protection),
            and <strong className="font-semibold text-[#2c2c2c]">Human Trust</strong> (human risk management for the AI era).
          </p>
        </RfPageFrame>
      </div>

      {/* Governance */}
      <section className="bg-white border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-semibold text-[#2c2c2c]">Governance</h2>
              <p className="mt-2 text-[0.9375rem] text-[#5c5c5c] max-w-2xl">
                Connect committees to evidence: AI governance workflows plus structured cybersecurity assessment.
              </p>
            </div>
            <Link
              to="/solutions/governance"
              className="text-[0.9375rem] font-medium text-[#005a9c] inline-flex items-center gap-1 shrink-0"
            >
              Governance hub <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <article className="rf-card p-6 md:p-8 bg-white">
              <h3 className="text-lg font-semibold text-[#2c2c2c] mb-2">AI governance</h3>
              <p className="text-[0.9375rem] text-[#5c5c5c] leading-relaxed mb-5">
                Policies, ownership, and review gates that stay legible outside engineering — via AiGov.
              </p>
              <Link to="/aigov" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[#005a9c]">
                AiGov <ArrowRight className="w-4 h-4" />
              </Link>
            </article>
            <article className="rf-card p-6 md:p-8 bg-white">
              <h3 className="text-lg font-semibold text-[#2c2c2c] mb-2">Cybersecurity assessment</h3>
              <p className="text-[0.9375rem] text-[#5c5c5c] leading-relaxed mb-5">
                Repeatable testing and traceable results for AI systems — delivered through ScanFort.
              </p>
              <Link to="/scanfort" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[#005a9c]">
                ScanFort <ArrowRight className="w-4 h-4" />
              </Link>
            </article>
          </div>
        </RfPageFrame>
      </section>

      {/* Security for AI */}
      <section className="bg-[#f0f7ff] border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-semibold text-[#2c2c2c]">Security for AI</h2>
              <p className="mt-2 text-[0.9375rem] text-[#5c5c5c] max-w-2xl">
                Deep inspection of what you ship and real-time protection for how it is used.
              </p>
            </div>
            <Link
              to="/solutions/security-for-ai"
              className="text-[0.9375rem] font-medium text-[#005a9c] inline-flex items-center gap-1 shrink-0"
            >
              Security for AI hub <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <article className="rf-card p-6 md:p-8 bg-white">
              <h3 className="text-lg font-semibold text-[#2c2c2c] mb-2">ModelFort</h3>
              <p className="text-[0.9375rem] text-[#5c5c5c] leading-relaxed mb-5">
                Analysis of models and artefacts to surface weaknesses before production.
              </p>
              <Link to="/modelfort" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[#005a9c]">
                ModelFort <ArrowRight className="w-4 h-4" />
              </Link>
            </article>
            <article className="rf-card p-6 md:p-8 bg-white">
              <h3 className="text-lg font-semibold text-[#2c2c2c] mb-2">GuardFort</h3>
              <p className="text-[0.9375rem] text-[#5c5c5c] leading-relaxed mb-5">
                AI firewall-style controls for prompts, pipelines, and abusive traffic.
              </p>
              <Link to="/guardfort" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[#005a9c]">
                GuardFort <ArrowRight className="w-4 h-4" />
              </Link>
            </article>
          </div>
        </RfPageFrame>
      </section>

      {/* Human Trust */}
      <section className="bg-white border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-semibold text-[#2c2c2c]">Human Trust</h2>
              <p className="mt-2 text-[0.9375rem] text-[#5c5c5c] max-w-2xl">
                Packaged solutions that make human judgment reliable in the AI era — risk assessment, managed simulations, and enterprise agent readiness.
              </p>
            </div>
            <Link
              to="/services/human-trust"
              className="text-[0.9375rem] font-medium text-[#005a9c] inline-flex items-center gap-1 shrink-0"
            >
              Human Trust services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <article className="rf-card p-6 md:p-8 flex flex-col bg-white">
              <div className="w-10 h-10 bg-[#E1F5EE] rounded-lg flex items-center justify-center mb-4">
                <BarChart2 className="w-5 h-5 text-[#0F6E56]" />
              </div>
              <h3 className="text-lg font-semibold text-[#2c2c2c] mb-2">TrustScore</h3>
              <p className="text-[0.9375rem] text-[#5c5c5c] leading-relaxed flex-1 mb-5">
                Human risk assessment across five dimensions — AI threat recognition, verification behaviour, reporting instincts, policy compliance, and shadow AI usage. Baseline, remediation, re-assessment. Board-ready report.
              </p>
              <Link to="/solutions/trustscore" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[#005a9c]">
                TrustScore <ArrowRight className="w-4 h-4" />
              </Link>
            </article>
            <article className="rf-card p-6 md:p-8 flex flex-col bg-white">
              <div className="w-10 h-10 bg-[#FAECE7] rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-5 h-5 text-[#993C1D]" />
              </div>
              <h3 className="text-lg font-semibold text-[#2c2c2c] mb-2">DeepStrike</h3>
              <p className="text-[0.9375rem] text-[#5c5c5c] leading-relaxed flex-1 mb-5">
                Managed AI attack simulation campaigns. Quarterly multi-vector exercises: prompt injection emails, deepfake vishing, synthetic identity probes. Fully managed with white-labelled reporting for your board and insurers.
              </p>
              <Link to="/solutions/deepstrike" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[#005a9c]">
                DeepStrike <ArrowRight className="w-4 h-4" />
              </Link>
            </article>
            <article className="rf-card p-6 md:p-8 flex flex-col bg-white">
              <div className="w-10 h-10 bg-[#E6F1FB] rounded-lg flex items-center justify-center mb-4">
                <Cpu className="w-5 h-5 text-[#185FA5]" />
              </div>
              <h3 className="text-lg font-semibold text-[#2c2c2c] mb-2">AgentReady</h3>
              <p className="text-[0.9375rem] text-[#5c5c5c] leading-relaxed flex-1 mb-5">
                Human readiness for enterprise AI agent rollouts — Microsoft 365 Copilot, Google Workspace AI, Salesforce Einstein, ServiceNow AI, and any other platform. Pre-deployment training, policies, and TrustScore baseline. Post-deployment: ongoing simulation and measurement.
              </p>
              <Link to="/solutions/copilotready" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[#005a9c]">
                AgentReady <ArrowRight className="w-4 h-4" />
              </Link>
            </article>
          </div>
        </RfPageFrame>
      </section>
    </>
  );
}
